// src/App.jsx
import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import usePageTracking from "./analytics/usePageTracking";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AppShell from "./components/AppShell";
import HomePage from "./pages/HomePage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import CompanyProfileManagementPage from "./pages/CompanyProfileManagementPage";
import JurisdictionsPage from "./pages/JurisdictionsPage";
import ProfilePage from "./pages/ProfilePage";
import VerifyCompanyEmailPage from "./pages/VerifyCompanyEmailPage";
import ProtectedRoute from "./components/ProtectedRoute";
import BenefitsPage from "./pages/BenefitsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ForecastPage from "./pages/ForecastPage";
import LearnPage from "./pages/learn/LearnPage";
import LearnArticlePage, { LearnArticleResearchCTA } from "./pages/learn/LearnArticlePage";
import ProductPlaceholderPage from "./pages/ProductPlaceholderPage";
import ScoringTransparencyPage from "./pages/ScoringTransparencyPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyAccountPage from "./pages/VerifyAccountPage";
import LoginPage from "./pages/LoginPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import NotFoundPage from "./pages/NotFoundPage";
import CookieBanner from "./components/CookieBanner";

const DEFAULT_TITLE = "ResourceNAV - Natural Resource Stocks - Mining Analytics";
const DEFAULT_DESCRIPTION =
  "ResourceNAV helps investors research natural resource and mining companies with financial, asset, jurisdiction, and ranking data.";

function setMetaTag(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(pathname) {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", `${window.location.origin}${pathname}`);
}

function getRouteMetadata(pathname) {
  if (pathname === "/rankings") {
    return {
      title: "Mining Company Rankings Coming Soon | ResourceNAV",
      description: "ResourceNAV mining company rankings are coming soon.",
    };
  }
  if (pathname.startsWith("/rankings/")) {
    return {
      title: "Mining Company Rankings Coming Soon | ResourceNAV",
      description: "ResourceNAV mining company rankings are coming soon.",
    };
  }
  if (pathname === "/companies") {
    return {
      title: "Mining Companies | ResourceNAV",
      description: "Browse public mining company profiles, commodities, assets, and financial data.",
    };
  }
  if (pathname === "/jurisdictions") {
    return {
      title: "Mining Jurisdictions | ResourceNAV",
      description: "Explore mining jurisdictions and company exposure by region.",
    };
  }
  if (pathname === "/screener") {
    return {
      title: "Mining Stock Screener Coming Soon | ResourceNAV",
      description: "The ResourceNAV mining stock screener is coming soon.",
    };
  }
  if (pathname === "/scoring-transparency") {
    return {
      title: "Scoring Transparency | ResourceNAV",
      description: "Internal scoring run transparency table for ResourceNAV model validation.",
    };
  }
  if (pathname === "/learn") {
    return {
      title: "Learn | ResourceNAV",
      description: "Understand the metrics, investing frameworks, and ResourceNAV tools used to analyze mining companies.",
    };
  }
  if (pathname.startsWith("/learn/")) {
    return {
      title: "ResourceNAV Learn",
      description: "ResourceNAV Learn article.",
    };
  }
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
}

export default function App() {
  const location = useLocation();
  usePageTracking();

  useEffect(() => {
    const metadata = getRouteMetadata(location.pathname);
    document.title = metadata.title;
    setMetaTag("description", metadata.description);
    setMetaTag("robots", "index,follow");
    setCanonical(location.pathname);
  }, [location.pathname]);
  
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div>
        <Header />
      </div>
      <div style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/companies" element={<AppShell><CompaniesPage /></AppShell>} />
          <Route path="/companies/:slug" element={<AppShell><CompanyProfilePage /></AppShell>} />
          <Route path="/companies/:slug/manage" element={<CompanyProfileManagementPage />} />
          <Route path="/jurisdictions" element={<AppShell><JurisdictionsPage /></AppShell>} />
          <Route path="/verify-company-email" element={<VerifyCompanyEmailPage />} />
          <Route path="/verify-account" element={<VerifyAccountPage />} />
          <Route path="/account/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/benefits" element={<BenefitsPage />} />
          <Route path="/forecast" element={<AppShell><ForecastPage /></AppShell>} />
          <Route path="/screener" element={<AppShell><ProductPlaceholderPage type="screener" /></AppShell>} />
          <Route
            path="/scoring-transparency"
            element={
              <ProtectedRoute>
                <AppShell fullWidth>
                  <ScoringTransparencyPage />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route path="/rankings" element={<AppShell><ProductPlaceholderPage type="rankings" /></AppShell>} />
          <Route path="/learn" element={<AppShell><LearnPage /></AppShell>} />
          <Route
            path="/learn/:slug"
            element={
              <AppShell fullWidthBottom={<LearnArticleResearchCTA />}>
                <LearnArticlePage />
              </AppShell>
            }
          />
          <Route
            path="/rankings/:slug"
            element={<AppShell><ProductPlaceholderPage type="rankings" /></AppShell>}
          />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/access-denied" element={<AppShell><AccessDeniedPage /></AppShell>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      <CookieBanner />
      <Footer />
    </div>
  );
}
